/**
 * ProductPage — `/products/:slug`
 *
 * Detail page for a Phase 17 ProductListing. Fetches by slug, renders
 * a hero + tabbed body. "Install Now" calls subscribe() and opens the
 * Stripe Checkout URL.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Card, CardSubtitle, CardTitle } from '@/components/Card';
import { ErrorState } from '@/components/ErrorState';
import { Spinner } from '@/components/LoadingState';
import { api } from '@/api/client';
import type { SubscribeRequest } from '@/api/types';

type Tab = 'overview' | 'pricing' | 'contract' | 'reviews' | 'metrics';

export function ProductPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const [tab, setTab] = useState<Tab>('overview');
  const [installing, setInstalling] = useState(false);
  const [installError, setInstallError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['product', slug],
    queryFn: () => api.getProductBySlug(slug),
    enabled: slug.length > 0,
  });

  if (query.isPending) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Spinner size="md" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="mx-auto max-w-2xl">
        <ErrorState
          title="Product not found"
          body={`No product with slug "${slug}" exists in the marketplace.`}
          actions={
            <Link to="/explore" className="contents">
              <button
                type="button"
                className="rounded-md border border-border bg-surface px-4 py-2 text-sm font-medium text-ink hover:border-border-strong"
              >
                Back to marketplace
              </button>
            </Link>
          }
        />
      </div>
    );
  }

  const product = query.data;
  const basePriceDollars = (product.price_usd_cents / 100).toFixed(2);

  async function handleInstall() {
    setInstalling(true);
    setInstallError(null);
    try {
      const req: SubscribeRequest = {
        customer_email: '',
        success_url: `${window.location.origin}/dashboard/earnings`,
        cancel_url: window.location.href,
      };
      const resp = await api.subscribe(product.slug, req);
      if (resp.checkout_url) {
        window.location.href = resp.checkout_url;
      } else if (resp.message) {
        setInstallError(resp.message);
      }
    } catch (err) {
      setInstallError(err instanceof Error ? err.message : 'Install failed.');
    } finally {
      setInstalling(false);
    }
  }

  const tabs: Tab[] = ['overview', 'pricing', 'contract', 'reviews', 'metrics'];
  const tabLabel: Record<Tab, string> = {
    overview: 'Overview',
    pricing: 'Pricing',
    contract: 'Contract',
    reviews: 'Reviews',
    metrics: 'Metrics',
  };

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <Link to="/explore" className="text-sm text-ink-muted hover:text-ink">
        Back to marketplace
      </Link>

      {/* Hero */}
      <header className="flex flex-col gap-5 rounded-xl border border-border bg-surface p-6 md:flex-row md:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full border border-border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
              {product.domain}
            </span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">{product.name}</h1>
          <p className="text-sm text-ink-muted">{product.tagline}</p>
          <p className="text-sm font-semibold text-ink">${basePriceDollars}/month</p>
        </div>

        <div className="flex flex-col items-end gap-2">
          <button
            type="button"
            onClick={handleInstall}
            disabled={installing}
            className="inline-flex items-center justify-center rounded-md bg-spark px-5 py-2.5 text-sm font-semibold text-white hover:bg-spark/90 disabled:opacity-60"
          >
            {installing ? 'Redirecting...' : 'Install Now'}
          </button>
          {installError ? (
            <p className="max-w-[240px] text-end text-xs text-danger">{installError}</p>
          ) : null}
        </div>
      </header>

      {/* Tab bar */}
      <nav aria-label={product.name} className="flex flex-wrap gap-1 border-b border-border">
        {tabs.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            aria-current={tab === k ? 'page' : undefined}
            className={
              tab === k
                ? 'border-b-2 border-spark px-3 py-2 text-sm font-medium text-ink'
                : 'border-b-2 border-transparent px-3 py-2 text-sm text-ink-muted hover:text-ink'
            }
          >
            {tabLabel[k]}
          </button>
        ))}
      </nav>

      {/* Tab panels */}
      {tab === 'overview' ? (
        <Card>
          <CardTitle>{product.name}</CardTitle>
          <CardSubtitle>{product.tagline}</CardSubtitle>
          <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink">
            {product.description}
          </p>
          {product.pricing_tiers[0]?.features && product.pricing_tiers[0].features.length > 0 ? (
            <ul className="mt-4 flex flex-col gap-1">
              {product.pricing_tiers[0].features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-ink">
                  <span className="text-spark" aria-hidden>
                    ✓
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          ) : null}
        </Card>
      ) : null}

      {tab === 'pricing' ? (
        <div className="grid gap-4 sm:grid-cols-3">
          {product.pricing_tiers.map((tier) => (
            <Card key={tier.name}>
              <CardTitle>{tier.name}</CardTitle>
              <p className="mt-1 text-lg font-semibold text-ink">
                ${(tier.price_usd_cents / 100).toFixed(2)}
                <span className="ms-1 text-xs font-normal text-ink-muted">/month</span>
              </p>
              <ul className="mt-3 flex flex-col gap-1.5">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-ink">
                    <span className="mt-0.5 text-spark" aria-hidden>
                      ✓
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      ) : null}

      {tab === 'contract' ? (
        <Card>
          <CardTitle>Contract</CardTitle>
          <CardSubtitle>Royalty split enforced at settlement by gi-royalty (M12).</CardSubtitle>
          <div className="mt-4 flex h-3 w-full overflow-hidden rounded-full">
            <div
              style={{
                width: `${product.royalty_split.author * 100}%`,
                backgroundColor: '#6366f1',
              }}
              aria-hidden
            />
            <div
              style={{
                width: `${product.royalty_split.engine * 100}%`,
                backgroundColor: '#a78bfa',
              }}
              aria-hidden
            />
            <div
              style={{
                width: `${product.royalty_split.foundation * 100}%`,
                backgroundColor: '#fbbf24',
              }}
              aria-hidden
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-xs">
            <SplitLegend color="#6366f1" label="Author" pct={Math.round(product.royalty_split.author * 100)} />
            <SplitLegend color="#a78bfa" label="Engine" pct={Math.round(product.royalty_split.engine * 100)} />
            <SplitLegend color="#fbbf24" label="Foundation" pct={Math.round(product.royalty_split.foundation * 100)} />
          </div>
        </Card>
      ) : null}

      {tab === 'reviews' ? (
        <Card>
          <CardTitle>Reviews</CardTitle>
          <p className="mt-2 text-sm text-ink-muted">
            Signed reviews only. Be the first to review.
          </p>
        </Card>
      ) : null}

      {tab === 'metrics' ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Card>
            <CardTitle>Installs</CardTitle>
            <p className="mt-1 text-2xl font-semibold text-ink">
              {product.installs > 0 ? product.installs.toLocaleString() : '—'}
            </p>
          </Card>
          <Card>
            <CardTitle>Avg rating</CardTitle>
            <p className="mt-1 text-2xl font-semibold text-ink">
              {product.avg_rating > 0 ? product.avg_rating.toFixed(1) : '—'}
            </p>
          </Card>
        </div>
      ) : null}
    </div>
  );
}

function SplitLegend({ color, label, pct }: { color: string; label: string; pct: number }) {
  return (
    <div className="inline-flex items-center gap-2">
      <span className="h-3 w-3 rounded-full" style={{ backgroundColor: color }} aria-hidden />
      <span className="text-ink-muted">
        {label} <span className="font-semibold text-ink">{pct}%</span>
      </span>
    </div>
  );
}
